<template>
  <button
    type="button"
    :disabled="disabled"
    :class="[
      'track-btn',
      [type || ''],
      [isBlock ? 'is-block' : ''],
      [customColor ? 'custom-color' : '']
    ]"
    :style="customStyle"
    @click="handleClick"
  >
    <span class="background"></span>
    <div
      v-if="icon"
      class="t-Button-Icon"
    >
      <Icon
        :name="icon"
        :size="iconSize"
        :handle-click="handleClick"
      />
    </div>
    <slot>
      {{ label }}
    </slot>
  </button>
</template>

<script>
import Icon from '../Icon/Icon.vue';

export default {
  name: 'Button',
  components: { Icon },
  props: {
    label: {
      type: String,
      default: '',
    },
    handleClick: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'tertiary', 'text', 'pill', 'icon'].includes(
        value.toLowerCase(),
      ),
    },
    customColor: {
      type: String,
      default: null,
    },
    isBlock: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    iconSize: {
      type: String,
      default: '16',
    },
  },
  computed: {
    customStyle() {
      let customStyle = '';

      if (this.customColor) {
        customStyle += `--track-btn-custom-color: ${this.customColor};`;
      }

      return customStyle;
    },
  },
};
</script>

<style scoped lang="scss">
.track-btn {
  position: relative;
  display: inline-block;
  cursor: pointer;
  user-select: none;
  line-height: 1;
  touch-action: manipulation;
  border: 0.5px solid;
  text-align: center;
  white-space: nowrap;
  font-weight: 600;
  font-size: 14px;
  border-radius: 3px;
  padding: 8px 16px;
  box-sizing: border-box;
  background-color: var(
    --track-btn-custom-color,
    var(--track-color-brand-primary)
  );
  border-color: var(--track-btn-custom-color, var(--track-color-brand-primary));
  color: var(--track-color-white);
  font-family: var(--track-font-family-base), sans-serif;
  svg {
      fill: var(--track-btn-custom-color, var(--track-color-brand-primary));
    }
    &:hover {
      svg {
        fill: var(--track-btn-custom-color, var(--track-color-brand-primary-darkest));
      }
    }
  &:hover {
    background-color: var(
      --track-btn-custom-color,
      var(--track-color-brand-primary-darkest)
    );
    color: var(--track-color-white);
  }
  &:disabled {
    cursor: not-allowed;
    color: var(--track-color-bluegray-40);
    border-color: var(--track-color-bluegray-10);
    background-color: var(--track-color-bluegray-10);
  }
  &.secondary {
    background-color: transparent;
    border-color: var(
      --track-btn-custom-color,
      var(--track-color-brand-primary)
    );
    color: var(--track-btn-custom-color, var(--track-color-brand-primary));
    &:hover {
      border-color: var(
        --track-btn-custom-color,
        var(--track-color-brand-primary-darkest)
      );
      color: var(
        --track-btn-custom-color,
        var(--track-color-brand-primary-darkest)
      );
      svg {
        fill: var(--track-btn-custom-color, var(--track-color-brand-primary-darkest)),
      }
    }
    &:disabled {
      cursor: not-allowed;
      border-color: var(--track-color-bluegray-40);
      background-color: transparent;
      color: var(--track-color-bluegray-40);
    }
  }
  &.tertiary {
    background-color: transparent;
    border-color: transparent;
    color: var(--track-btn-custom-color, var(--track-color-brand-primary));
    &:hover {
      border-color: var(
        --track-btn-custom-color,
        --track-color-brand-primary-darkest
      );
      color: var(
        --track-btn-custom-color,
        var(--track-color-brand-primary-darkest)
      );
    }
    &:disabled {
      background-color: transparent;
      border-color: transparent;
      color: var(--track-color-bluegray-50);
    }
  }
  &.text {
    background-color: transparent;
    border-color: transparent;
    color: var(--track-color-brand-primary);
    padding: 0;
    &:hover {
      color: var(--track-color-brand-primary-darkest);
    }
    &:disabled {
      color: var(--track-color-bluegray-50);
    }
  }
  &.pill {
    background-color: transparent;
    border-color: transparent;
    border-radius: 22px;
    color: var(--track-color-bluegray-70);
    padding: var(--track-spacing-sm);
    &.is-active,
    &:hover {
      background-color: var(--track-color-bluegray-10);
    }
    &:disabled {
      color: var(--track-color-bluegray-50);
    }
  }
  &.icon {
    background-color: transparent;
    border-color: transparent;
    border-radius: var(--track-spacing-xs);
    color: var(--track-color-bluegray-80);
    padding: var(--track-spacing-xs);
    &.is-active,
    &:hover {
      background-color: var(--track-color-bluegray-10);
    }
    &:disabled {
      color: var(--track-color-bluegray-50);
    }
  }
  &.is-block {
    width: 100%;
  }
  &.custom-color:hover {
    filter: brightness(0.8) saturate(1.4);
  }
}
.t-Button-Icon {
    display: inline-block;
    vertical-align: middle;
  }

</style>
